import LoadingButton from '@atlaskit/button/loading-button';
import Form, { FormHeader, FormSection } from '@atlaskit/form';
import {
  BillingDetails,
  isAccess,
  useNotification,
  useUpdateTransactionAccount,
  successNotificationBuilder,
  NotificationTypes,
  useFormSubmit,
  errorNotificationBuilder,
  InfoPanel,
  ErrorComponents,
  InflightInvoiceBanner,
  withApollo,
  shipToAddressesType,
  getTaxIds,
  getFallbackTaxId,
} from '@css-xp/base';
import { BillingAdmin, CommerceAddress, generatedTypesNew } from '@css-xp/graphtypes';
import { CreditCard } from '@css-xp/graphtypes/src/generated-types-new';
import { useQuery } from '@apollo/client';
import { fetchShippingAddressesForTransactionAccount } from '../../../tabs/invoice-group-tabs';

import {
  setPricingEligibility,
  UpdateTransactionAccountPayload,
  FormattedResponseFromCCP,
  BillToAndShipToParties,
} from '@atlassian/cssxp-types-billing';
import { defaultTo, isNil, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IsoSelectItem } from '@css-xp/base/forms/src/address-form-section';
import UpdateBillingAdminsFields from './ceate-txa-components/update-billing-admins-fields';
import { Permissions } from '@atlassian/cssxp-typescommon';
import ChangePricingEligibility from '../component/update-pricing-eligibility';
import { updatePricingEligibility } from '@atlassian/temp-proxies';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { SelectNewBillToAddress } from './select-new-bill-to-address';

interface FormData {
  billToName?: string | null;
  billToAddress1?: string | null;
  billToAddress2?: string | null;
  billToCity?: string | null;
  billToCountry?: IsoSelectItem | null;
  billToPostalCode?: string | null;
  billToState?: IsoSelectItem | null;
  billToTaxId?: string | null;
  billToGst?: string | null;
  billToPst?: string | null;

  shipToName?: string | null;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: IsoSelectItem | null;
  shipToPostalCode?: string | null;
  shipToState?: IsoSelectItem | null;
  shipToTaxId?: string | null;

  recipients?: string[];

  pricingPlan?: string;
}

interface Props {
  transactionAccountId?: string;
  billToAddress?: CommerceAddress | null;
  shipToAddress?: CommerceAddress | null;
  invoiceGroupId?: string;
  billingAdmins?: (BillingAdmin | undefined | null)[];
  isShipToAddressViewable?: boolean | true;
  onClose: () => void;
  /** Logic to run after a failed or successful attempt, such as refreshing data */
  onComplete: () => void;
  billToAddressHeading?: string;
  billToPartyPricingEligibilty?: string | null;
  availablePaymentMethods?: CreditCard[];
  isEditable?: boolean | true;
  isPartner?: boolean;
}

const EditBillingDetailsForm = withApollo(
  ({
    transactionAccountId,
    billToAddress: billingAdress,
    shipToAddress,
    invoiceGroupId,
    billingAdmins = [],
    isShipToAddressViewable = true,
    onClose,
    onComplete: onUpdatcomplete,
    billToPartyPricingEligibilty,
    availablePaymentMethods,
    isEditable = true,
    isPartner,
  }: Props) => {
    const { sendSuccessNotification, notificationSent, setNotificationSent, sendErrorNotification } = useNotification();
    const [isError, setError] = React.useState<boolean>(false);
    const [errMsgList, setErrMsgList] = React.useState<string>();
    const notificationOptions = successNotificationBuilder().isVersion(NotificationTypes.FLAG);
    const [currentBillingAddress, setCurrentBillingAddress] = React.useState(billingAdress);
    const { data, loading } = useQuery<
      generatedTypesNew.FetchShipToPartiesForTransactionAccountQuery,
      generatedTypesNew.FetchShipToPartiesForTransactionAccountQueryVariables
    >(fetchShippingAddressesForTransactionAccount, {
      variables: { id: transactionAccountId || '' },
      fetchPolicy: 'no-cache',
    });

    const onComplete = () => {
      const notiication = notificationOptions
        .withId('success-billing-admins')
        .withHeader('Updated billing account.')
        .build();

      sendSuccessNotification(notiication);
      setNotificationSent(true);
      onUpdatcomplete();
      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.UPDATED,
        actionSubject: AnalyticsTrackSubject.BILLING_ACCOUNT,
      });
    };

    const onError = useCallback(
      (errorList?: (() => JSX.Element)[]) => {
        setError(true);
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Error updating billing account');

        if (errorList) {
          const message = errorList.map((DetailItem, index) => (
            <>
              <DetailItem key={index} />
            </>
          ));
          notificationOptions.withMessage(message);
        }
        const notification = notificationOptions.build();
        sendErrorNotification(notification);
        setNotificationSent(true);
      },
      [sendErrorNotification, setNotificationSent]
    );

    const errorMsgList = (errList?: FormattedResponseFromCCP[]) => {
      const errMsg = errList?.map((error) => {
        let errorMessages = [];
        if (error.error?.payload?.validationErrors) {
          errorMessages = error.error?.payload?.validationErrors.map((message: any) => `${message?.validationMessage}`);
        } else {
          errorMessages.push(error.error?.message ?? '');
        }
        return errorMessages;
      });
      setErrMsgList(errMsg?.join(','));
    };
    const {
      isProcessing: isLoadingBillToParty,
      updateTransactionAccount: submitBillToParty,
    } = useUpdateTransactionAccount({ onClose, onComplete, sendNotification: false, onError, errorMsgList });
    const {
      isProcessing: isLoadingBillingAdmin,
      updateTransactionAccount: submtBillingAdmin,
    } = useUpdateTransactionAccount({ onClose, onComplete, sendNotification: false, onError, errorMsgList });
    const {
      error: formPricingError,
      isProcessing: isSubmittingPricing,
      submitForm: submitPricingEligibility,
      complete: isUpdatePricingComplete,
    } = useFormSubmit<setPricingEligibility, any>({
      submitFormAction: updatePricingEligibility,
    });

    const [adminFieldsDirty, setAdminsDirtyFlag] = useState(false);

    // Need this since we can't call hook from the nested function submitBillingDetails.
    const addBillingAdminPermission = isAccess(Permissions.CONTACT_WRITE);

    // To track if account creation is pending in UpdateBillingAdminsFields and save button should be disabled.
    const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);

    const updateAdminFieldsDirtyFlag = (flag: boolean) => {
      setAdminsDirtyFlag(flag);
    };

    const submitBillingAdmins = (billingData: Partial<FormData>) => {
      setError(false);
      setErrMsgList('');
      const billingRecipients = [];
      for (const key in billingData) {
        if (key.includes('recipients')) {
          // @ts-ignore
          billingRecipients.push(billingData[key]);
        }
      }
      const billingAdminsOnForm = defaultTo(billingRecipients, []);
      const billingAdminsOnFile = billingAdmins
        // @ts-ignore
        ?.map((item: { email: string }) => item.email)
        .filter((emailOrAaid) => !isNil(emailOrAaid));

      const newAddedBillingAdminsList = billingAdminsOnForm.filter(
        (admin: string) => !billingAdminsOnFile?.includes(admin)
      );

      const deletedBillingAdminsList = billingAdminsOnFile?.filter(
        (admin: string) => !billingAdminsOnForm.includes(admin) && !isNil(admin)
      );
      const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
        modifiedBillingAdmins: {
          emailsOrAaidsToAdd: addBillingAdminPermission ? newAddedBillingAdminsList : [], // to check if their is a permission to add billing admin.
          emailsOrAaidsToDelete: deletedBillingAdminsList,
        },
      };
      submtBillingAdmin({
        transactionAccountId: defaultTo(transactionAccountId, ''),
        updateTransactionAccountPayload,
      });
    };

    const submitBillToDetails = (billingData: Partial<FormData>) => {
      setError(false);
      setErrMsgList('');

      const taxIds = getTaxIds(billingData);

      const billToAddress: BillToAndShipToParties = {
        name: defaultTo(billingData.billToName, ''),
        postalAddress: {
          line1: defaultTo(billingData.billToAddress1, ''),
          line2: defaultTo(billingData.billToAddress2 || null, null),
          city: defaultTo(billingData?.billToCity, ''),
          state: defaultTo(billingData?.billToState?.value, null),
          postcode: defaultTo(billingData?.billToPostalCode, ''),
          country: defaultTo(billingData?.billToCountry?.value, ''),
        },
        taxId: billingData?.billToTaxId || getFallbackTaxId(taxIds),
      };

      if (taxIds.length > 0) {
        billToAddress.taxIds = taxIds;
      }

      const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
        invoiceGroupId,
        billToAddress,
      };

      submitBillToParty({
        transactionAccountId: defaultTo(transactionAccountId, ''),
        updateTransactionAccountPayload,
      });
    };

    const submitPricingPlan = (billingData: Partial<FormData>) => {
      setError(false);
      setErrMsgList('');
      const pricingEligibility = {
        [defaultTo(billingData?.pricingPlan, 'commercial')]: 'true',
      };
      submitPricingEligibility({
        transactionAccountId,
        pricingEligibility,
      });
    };

    const getFormSectionSubmit = ({
      label,
      isLoading,
      testId,
    }: {
      label: string;
      isLoading?: boolean;
      testId?: string;
    }) => {
      return (
        <div className="btn-primary">
          <LoadingButton type="submit" testId={testId} appearance="primary" isLoading={isLoading}>
            {label}
          </LoadingButton>
        </div>
      );
    };

    const handleAddressSelect = (address: { label: HTMLDivElement; value: CommerceAddress }) => {
      setCurrentBillingAddress(address?.value);
    };

    const defaultPayment: CreditCard | undefined = useMemo(() => {
      return availablePaymentMethods?.find((paymenyMethod) => {
        return paymenyMethod.default;
      });
    }, [availablePaymentMethods]);

    const BillingAdressForm = React.useMemo(() => {
      return (
        <Form onSubmit={submitBillToDetails}>
          {({ formProps, setFieldValue }) => (
            <form {...formProps}>
              <FormSection>
                <SelectNewBillToAddress
                  defaultPayment={defaultPayment}
                  shipToAddresses={data as shipToAddressesType}
                  onSelect={handleAddressSelect}
                  isEditable={isEditable}
                />
                <div className="section">
                  <BillingDetails
                    billToAddress={currentBillingAddress}
                    shipToAddress={shipToAddress}
                    isEditable={isEditable}
                    setFieldValue={setFieldValue}
                    isShipToAddressViewable={isShipToAddressViewable}
                    billToAddressHeading={'Update billing address'}
                  />
                </div>
                {getFormSectionSubmit({
                  label: 'Update billing address',
                  isLoading: isLoadingBillToParty,
                  testId: 'billtopartysubmit',
                })}
              </FormSection>
            </form>
          )}
        </Form>
      );
    }, [isLoadingBillToParty, loading, currentBillingAddress]);

    const BillingAdminsForm = React.useMemo(() => {
      return (
        <Form onSubmit={submitBillingAdmins}>
          {({ formProps }) => (
            <form {...formProps}>
              <FormSection>
                <div className="section">
                  <UpdateBillingAdminsFields
                    // @ts-ignore
                    recipients={recipients}
                    updateAdminFieldsDirtyFlag={updateAdminFieldsDirtyFlag}
                    disableSaveButton={(status: boolean) => {
                      setDisableSaveButton(status);
                    }}
                    isRequired={true}
                    placeholder={'Atlassian Account email address'}
                    helperMessage={
                      'To ensure access to billing info at all times, we recommend that billing accounts have more than one billing admin.'
                    }
                    title={'Billing admins'}
                    allowToAddBillingAdmin={addBillingAdminPermission} // for checking is user has enough permissions to add billign admin
                  />
                </div>
                {getFormSectionSubmit({
                  label: 'Update billing admins',
                  isLoading: isLoadingBillingAdmin,
                  testId: 'billingadminsubmit',
                })}
              </FormSection>
            </form>
          )}
        </Form>
      );
    }, [isLoadingBillingAdmin]);

    const PricingEligibilityForm = React.useMemo(() => {
      return (
        <Form onSubmit={submitPricingPlan}>
          {({ formProps }) => (
            <form {...formProps}>
              <FormSection>
                <div className="section">
                  <ChangePricingEligibility pricingEligibility={billToPartyPricingEligibilty} />
                </div>
                {getFormSectionSubmit({
                  label: 'Update pricing eligibility',
                  isLoading: isSubmittingPricing,
                  testId: 'pricingeligibilitysubmit',
                })}
              </FormSection>
            </form>
          )}
        </Form>
      );
    }, [isSubmittingPricing]);

    if (!transactionAccountId) return <p>No transaction account ID found</p>;

    if (isUpdatePricingComplete && !notificationSent) {
      onComplete();
      setNotificationSent(true);
    }

    useEffect(() => {
      if (formPricingError) {
        onError([() => <p>{formPricingError}</p>]);
      }
    }, [formPricingError, onError]);

    const recipients = !isEmpty(billingAdmins) ? billingAdmins.map((billingAdmin) => billingAdmin?.email) : [''];

    return (
      <div className="pane-sidebar-content" style={{ maxWidth: '600px', padding: '25px' }}>
        <FormHeader title="Edit billing account"></FormHeader>
        <InflightInvoiceBanner
          transactionAccountId={transactionAccountId}
          message={
            'We’re getting some invoices ready for this billing account. If you make changes, the orders within the generating invoices will be affected.'
          }
          type="information"
        />
        <FormSection>
          {isPartner && (
            <InfoPanel type="error" message={"You can't edit billing addresses for a partner account"}></InfoPanel>
          )}
        </FormSection>
        <FormSection>
          {(formPricingError || isError || errMsgList) && (
            <InfoPanel
              type="error"
              message={
                <ErrorComponents.ErrorMessageOnInfoPanel
                  title="We're unable to create billing account for the following reason"
                  message={formPricingError || errMsgList || 'Something went wrong.'}
                />
              }
            />
          )}
        </FormSection>
        <div>
          <FormSection>{BillingAdressForm}</FormSection>
          <FormSection>{BillingAdminsForm}</FormSection>
          <FormSection>{PricingEligibilityForm}</FormSection>
        </div>
      </div>
    );
  }
);

export default EditBillingDetailsForm;
